import {
  useMemo,
  useState,
  useRef,
  useCallback,
  ChangeEvent,
  useEffect,
} from 'react';

import { CellProps } from 'react-table';
import { useRecoilValue } from 'recoil';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { purgeProject, getAnnotatorList } from 'src/api/projects';
import { Vertical } from 'src/components/Alignments';
import JobAssignDialog, {
  ASSIGNMENT_MODE,
} from 'src/components/Modal/JobAssignDialog';
import PurgeAnnotatorDialog from 'src/components/Modal/PurgeAnnotatorDialog';
import { TableWrapper } from 'src/components/SharedTableStyles';
import Table from 'src/components/Table';
import useAlertSnackbar from 'src/hooks/useAlertSnackbar';
import useDeferredState from 'src/hooks/useDeferredState';
import { projectState, useRefreshProject } from 'src/states/project';
import { Modality } from 'src/types/api/data/image';
import { AnnotatorSchema } from 'src/types/api/data/project';
import { JobsStatsState } from 'src/types/client/stats';
import { Annotator } from 'src/types/client/user';
import { DEFAULT_PAGE_SIZE } from 'src/utils/constants';

interface AnnotatorsProps {
  projectId: string;
  modality: Modality;
  jobStatsState?: JobsStatsState;
  annotators?: AnnotatorSchema[];
}

export default function Annotators({
  projectId,
  modality,
  jobStatsState,
  annotators,
}: AnnotatorsProps): JSX.Element {
  const { openAlertSnackbar } = useAlertSnackbar();

  const project = useRecoilValue(projectState.project({ projectId, modality }));
  const refreshProject = useRefreshProject();

  const targetAnnotator = useRef<Annotator>();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [annotatorList, setAnnotatorList] = useState<Annotator[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);

  const refreshAnnotators = useCallback(() => {
    setRefreshCounter(prev => prev + 1);
  }, []);

  // Load annotator list with the provided annotators from parent
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setIsError(false);

    getAnnotatorList(projectId, modality, annotators)
      .then(list => {
        if (isMounted) {
          setAnnotatorList(list);
          setIsLoading(false);
          setIsError(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setAnnotatorList([]);
          setIsLoading(false);
          setIsError(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [projectId, modality, annotators, refreshCounter]);
  // Pagination
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(annotatorList.length / DEFAULT_PAGE_SIZE);

  // Controller dialogs
  const [openJobAssignDialog, setOpenJobAssignDialog] = useDeferredState(false);
  const [openJobUnassignDialog, setOpenJobUnassignDialog] =
    useDeferredState(false);
  const [openPurgeDialog, setOpenPurgeDialog] = useDeferredState(false);

  const handleOpenMenu = useCallback(
    (annotator: Annotator) => (event: React.MouseEvent<HTMLElement>) => {
      targetAnnotator.current = annotator;
      setMenuAnchorEl(event.currentTarget);
    },
    []
  );

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleClickAssign = useCallback(() => {
    setOpenJobAssignDialog(true);
    handleCloseMenu();
  }, [setOpenJobAssignDialog, handleCloseMenu]);

  const handleClickUnassign = useCallback(() => {
    setOpenJobUnassignDialog(true);
    handleCloseMenu();
  }, [setOpenJobUnassignDialog, handleCloseMenu]);

  const handleClickPurge = useCallback(() => {
    setOpenPurgeDialog(true);
    handleCloseMenu();
  }, [setOpenPurgeDialog, handleCloseMenu]);

  const handleConfirmPurge = useCallback(async () => {
    const annotator = targetAnnotator.current;
    if (!annotator) return;

    try {
      await purgeProject(projectId, modality, annotator.id);

      openAlertSnackbar({
        severity: 'success',
        description: `Successfully purged annotator ${annotator.username}`,
      });

      refreshProject();
      refreshAnnotators();
    } catch {
      openAlertSnackbar({
        severity: 'error',
        description: `Failed to purge annotator ${annotator.username}`,
      });
    }

    setOpenPurgeDialog(false);
  }, [
    projectId,
    modality,
    openAlertSnackbar,
    refreshProject,
    refreshAnnotators,
    setOpenPurgeDialog,
  ]);

  const handleClosePurgeDialog = useCallback(() => {
    setOpenPurgeDialog(false);
  }, [setOpenPurgeDialog]);

  const handleCloseJobAssignDialog = () => {
    setOpenJobAssignDialog(false);
  };

  const handleCloseJobUnassignDialog = () => {
    setOpenJobUnassignDialog(false);
  };

  const handleUpdateJobAssign = () => {
    refreshProject();
    refreshAnnotators();
    setOpenJobAssignDialog(false);
  };

  const handleUpdateJobUnassign = () => {
    refreshProject();
    refreshAnnotators();
    setOpenJobUnassignDialog(false);
  };

  const getUnassignableCount = useCallback(
    (annotator?: Annotator) =>
      (annotator?.jobStat.assignedStage ?? 0) +
      (annotator?.jobStat.queuedStage ?? 0),
    []
  );

  // Table elements
  const annotatorsColumnHeader = useMemo(() => {
    return [
      {
        Header: 'Annotator Username',
        accessor: 'username' as const,
      },
      {
        Header: 'Annotation Progress (Done / Total)',
        id: 'progress',
        accessor: ({ jobStat: { completedStage, total } }: Annotator) =>
          `${completedStage} / ${total}`,
        style: { textAlign: 'center' },
      },
      {
        Header: 'Cross Annotation Progress (Done / Total)',
        id: 'crossAnnotationProgress',
        accessor: ({ crossJobStat: { completedStage, total } }: Annotator) =>
          `${completedStage} / ${total}`,
        style: { width: '25%', textAlign: 'center' },
      },
      {
        Header: '',
        id: 'actions',
        style: { width: '80px', textAlign: 'center' },
        Cell: (cell: CellProps<Annotator, Annotator>) => {
          return (
            <IconButton
              size="small"
              onClick={handleOpenMenu(cell.row.original)}
            >
              <MoreVertIcon />
            </IconButton>
          );
        },
      },
    ];
  }, [handleOpenMenu]);

  const paginatedAnnotators: Annotator[] = useMemo(
    () =>
      annotatorList.slice(
        (page - 1) * DEFAULT_PAGE_SIZE,
        page * DEFAULT_PAGE_SIZE
      ),

    [annotatorList, page]
  );

  const handleChangePage = (event: ChangeEvent<unknown>, page: number) =>
    setPage(page);

  return (
    <Vertical>
      <Typography variant="body1" color="textSecondary">
        Manage annotators and their assignment status for this project.
      </Typography>
      <TableWrapper>
        <Table<Annotator>
          columns={annotatorsColumnHeader}
          data={paginatedAnnotators}
          isLoading={isLoading}
          isError={isError}
          onRefresh={refreshAnnotators}
          paginationProps={{
            page,
            count: totalPages,
            onChange: handleChangePage,
          }}
          style={{ marginTop: 0 }}
        />
      </TableWrapper>
      <JobAssignDialog
        mode={ASSIGNMENT_MODE.ASSIGN}
        open={openJobAssignDialog}
        annotatorName={targetAnnotator.current?.username}
        annotatorId={targetAnnotator.current?.id}
        projectId={projectId}
        assignableJobCount={project.jobStats.annotation.createdStage}
        currentJobCount={targetAnnotator.current?.jobStat.total ?? 0}
        onClose={handleCloseJobAssignDialog}
        onUpdate={handleUpdateJobAssign}
      />
      <JobAssignDialog
        mode={ASSIGNMENT_MODE.UNASSIGN}
        open={openJobUnassignDialog}
        annotatorName={targetAnnotator.current?.username}
        annotatorId={targetAnnotator.current?.id}
        projectId={projectId}
        assignableJobCount={getUnassignableCount(targetAnnotator.current)}
        currentJobCount={targetAnnotator.current?.jobStat.total ?? 0}
        onClose={handleCloseJobUnassignDialog}
        onUpdate={handleUpdateJobUnassign}
      />
      <PurgeAnnotatorDialog
        open={openPurgeDialog}
        annotatorName={targetAnnotator.current?.username ?? ''}
        projectName={project.name}
        jobCount={targetAnnotator.current?.jobStat.total ?? 0}
        onConfirm={handleConfirmPurge}
        onCancel={handleClosePurgeDialog}
      />
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={handleClickAssign}
          disabled={project.confirmed || jobStatsState === 'noJobLeft'}
        >
          Assign
        </MenuItem>
        <MenuItem
          onClick={handleClickUnassign}
          disabled={
            project.confirmed ||
            getUnassignableCount(targetAnnotator.current) === 0
          }
        >
          Unassign
        </MenuItem>
        <MenuItem onClick={handleClickPurge} sx={{ color: 'error.main' }}>
          Purge
        </MenuItem>
      </Menu>
    </Vertical>
  );
}
